import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { Box, Button, CircularProgress, InputAdornment, TextField, ToggleButton } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import AddIcon from '@mui/icons-material/Add'
import { gql, useApolloClient, useMutation, useQuery, useSubscription } from '@apollo/client'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import 'react-notifications/lib/notifications.css'
import {
  getActiveOrdersWithPagination,
  getDispatchAllOrders,
  getDispatchOrderCounts,
  getDispatchOrderItems,
  getDispatchRestaurantOrders,
  getRestaurantDispatchOrderCounts,
  subscribePlaceOrder,
  updateStatus,
  assignRider,
  dispatchOrderToFirstDelivery,
  sendOrderToRushmyfoodDelivery,
  getConfiguration,
} from '../apollo'
import ConfigurableValues from '../config/constants'
import { AGE, C, FONT, MONO, primaryButtonSx, tnum } from '../components/Dispatch/tokens'
import {
  CHANNELS, DAY, EMPTY_FILTERS, FIRST_DIR, FULFILMENT_TABS, REFRESH_MS, STATUS_TABS, applyFilters, canAssign, canMoveTo, canSendToFirstDelivery, canSendToRushmyfood,
  computeKpis, downloadCsv, filtersOn, fulfilmentCounts, isActive, isInTransit, isLate, isStale, localCounts, matchesTab, ms, needsDriver,
  sortViews, startOfDay, statusMeta, toCsv, toView, urgencyRank, COLUMNS, SPLIT_HIDDEN,
} from '../components/Dispatch/model'
import { DispatchUiContext } from '../components/Dispatch/context'
import KpiStrip from '../components/Dispatch/KpiStrip'
import StatusTabs from '../components/Dispatch/StatusTabs'
import TypeTabs from '../components/Dispatch/TypeTabs'
import FilterBar from '../components/Dispatch/FilterBar'
import DispatchTable, { BlankState } from '../components/Dispatch/DispatchTable'
import OrderDrawer from '../components/Dispatch/OrderDrawer'
import BulkActionBar from '../components/Dispatch/BulkActionBar'
import DispatchMap, { MapLegend } from '../components/Dispatch/DispatchMap'
import LiveIndicator from '../components/Dispatch/LiveIndicator'
import { CancelDialog, SendToFdDialog, SendToRmfDialog } from '../components/Dispatch/dialogs'
import NewDeliveryDialog from '../components/Dispatch/NewDeliveryDialog'
import { OLD_APP_LABEL, courierToView, ehungryToView } from '../components/Dispatch/externalViews'

const UPDATE_STATUS = gql`${updateStatus}`
const ASSIGN_RIDER = gql`${assignRider}`
const DISPATCH_TO_FD = gql`${dispatchOrderToFirstDelivery}`
const SEND_TO_RMF = gql`${sendOrderToRushmyfoodDelivery}`
const GET_ACTIVE_ORDERS = gql`${getActiveOrdersWithPagination}`
const GET_ALL_ORDERS = gql`${getDispatchAllOrders}`
const GET_COUNTS = gql`${getDispatchOrderCounts}`
const GET_ORDER_ITEMS = gql`${getDispatchOrderItems}`
const GET_CONFIG = gql`${getConfiguration}`
const GET_RESTAURANT_ORDERS = gql`${getDispatchRestaurantOrders}`
const GET_RESTAURANT_COUNTS = gql`${getRestaurantDispatchOrderCounts}`
const SUB_NEW_ORDER = gql`${subscribePlaceOrder}`

// Orders from outside the marketplace — old-app (eHungry) and email (courier)
// orders — with every field the dispatch drawer shows. A restaurant's board
// reads its own; the super admin board reads every restaurant's.
const EHUNGRY_ORDER_FIELDS = `
  _id remoteId externalId status orderType restaurantId
  placedAt readyAt processedAt
  restaurantName customerName customerPhone
  deliveryAddress deliveryNote notesToDriver specialInstructions
  total subtotal tax taxLabel tip deliveryCharge additionalFee
  couponAmount couponName discount paymentDescription
  assignedCourierName
  items { name variant category quantity unitPrice totalPrice specialInstructions forGuest addons { name choice portion quantity price totalPrice } }
  courier {
    provider status fee
    assignedAt acceptedAt arrivedOriginAt pickedUpAt arrivedDropoffAt droppedOffAt cancelledAt
    pickupEstimate dropoffEstimate driverName driverPhone driverPhoto driverTransport trackerLink dspTrackerLink dropoffPhoto
  }
`
const EMAIL_ORDER_FIELDS = `
  _id externalOrderNumber orderKind status
  customerName customerPhone
  dropoffAddress promiseTime
  subtotal deliveryFee processingFee tax tip total
  isPaid paymentMethod
  fdOrderId fdDriverName lastFdEventAt failureReason
  merchantId merchantName merchantProvider merchantAddress merchantLatitude merchantLongitude
  dropoffLatitude dropoffLongitude
  fdDriverPhone fdDriverTransport fdDspName
  fdTrackerLink fdDspTrackerLink
  fdPickupEta fdDropoffEta fdDropoffPhoto
  createdAt
  items { _id position title quantity unitPrice modifiers }
`
const RESTAURANT_EHUNGRY_ORDERS = gql(`
  query DispatchEhungryOrders($restaurantId: ID) {
    ehungryOrders(restaurantId: $restaurantId, orderType: "DELIVERY", limit: 200) { orders { ${EHUNGRY_ORDER_FIELDS} } }
  }
`)
const ADMIN_EHUNGRY_ORDERS = gql(`
  query DispatchAdminEhungryOrders {
    adminEhungryOrders(orderType: "DELIVERY", limit: 200) { orders { ${EHUNGRY_ORDER_FIELDS} } }
  }
`)
const RESTAURANT_EMAIL_ORDERS = gql(`
  query DispatchEmailOrders($restaurantId: ID) {
    restaurantCourierOrders(restaurantId: $restaurantId, limit: 200) { orders { ${EMAIL_ORDER_FIELDS} } }
  }
`)
const ADMIN_EMAIL_ORDERS = gql(`
  query DispatchAdminEmailOrders {
    courierOrders(limit: 200) { orders { ${EMAIL_ORDER_FIELDS} } }
  }
`)
// Which restaurant each email merchant is — the super admin board's only way to put an email order under its restaurant.
const COURIER_MERCHANT_RESTAURANTS = gql`
  query DispatchCourierMerchants {
    courierMerchants { _id rushmyfoodRestaurantId }
  }
`
const EHUNGRY_UPDATE_STATUS = gql`
  mutation DispatchEhungryUpdateStatus($id: ID!, $status: String!) {
    ehungryUpdateOrderStatus(id: $id, status: $status) { _id status processedAt }
  }
`
// The board's own restaurant, for the drawer's map pin and contact row on old-app and email orders.
const RESTAURANT_HOME = gql`
  query DispatchRestaurantHome($id: String) {
    restaurant(id: $id) { _id timezone name image address phone selfDelivery location { coordinates } zone { _id } }
  }
`
// The old-app mirror syncs every 60 s; email orders move on First Delivery's updates.
const EXTERNAL_REFRESH_MS = 60000

/**
 * Where the board's orders come from. The super admin board spans the whole
 * platform; a restaurant's board is the same screen over that restaurant's
 * orders (the server checks the caller controls it). `pageVars` takes a
 * 0-based page.
 */
function boardSource(restaurantId) {
  if (!restaurantId) {
    return {
      activeVars: { page: 0, rowsPerPage: 100000, search: '' },
      counts: GET_COUNTS,
      countsVars: {},
      pages: GET_ALL_ORDERS,
      pageVars: (page, rows) => ({ page, rows }),
      pageOf: data => data?.getOrdersByVendor ?? [],
      ehungry: ADMIN_EHUNGRY_ORDERS,
      ehungryOf: data => data?.adminEhungryOrders?.orders ?? [],
      email: ADMIN_EMAIL_ORDERS,
      emailOf: data => data?.courierOrders?.orders ?? [],
      externalVars: {},
    }
  }
  return {
    activeVars: { page: 0, rowsPerPage: 100000, search: '', restaurantId },
    counts: GET_RESTAURANT_COUNTS,
    countsVars: { restaurantId },
    pages: GET_RESTAURANT_ORDERS,
    // ordersByRestId counts pages from 1.
    pageVars: (page, rows) => ({ restaurant: restaurantId, page: page + 1, rows }),
    pageOf: data => data?.ordersByRestId ?? [],
    ehungry: RESTAURANT_EHUNGRY_ORDERS,
    ehungryOf: data => data?.ehungryOrders?.orders ?? [],
    email: RESTAURANT_EMAIL_ORDERS,
    emailOf: data => data?.restaurantCourierOrders?.orders ?? [],
    externalVars: { restaurantId },
  }
}

// Old-app and email deliveries sit among the rows on both boards, and on a
// tab each (`source`: filters rows, needs no history).
const BOARD_TABS = [
  ...STATUS_TABS,
  { key: 'OLD_APP', label: OLD_APP_LABEL, tone: 'muted', source: true },
  { key: 'EMAIL', label: 'Email orders', tone: 'muted', source: true },
]
// Every row on a restaurant's board is that restaurant.
const RESTAURANT_COLUMNS = COLUMNS.filter(c => c.id !== 'restaurant')
// Restaurants never see First Delivery by name: to them, a delivery it carries is RushMyFood's.
const RESTAURANT_TYPE_TABS = FULFILMENT_TABS.filter(t => t.key !== 'FIRST_DELIVERY')
const RESTAURANT_CHANNELS = Object.fromEntries(Object.entries(CHANNELS).filter(([k]) => k !== 'FIRST_DELIVERY'))
const seenByRestaurant = v =>
  v.channel === 'FIRST_DELIVERY' || v.fulfilment === 'FIRST_DELIVERY'
    ? {
        ...v,
        channel: v.channel === 'FIRST_DELIVERY' ? 'RUSHMYFOOD' : v.channel,
        fulfilment: v.fulfilment === 'FIRST_DELIVERY' ? 'RUSHMYFOOD' : v.fulfilment,
      }
    : v
/** Old-app and email rows have namespaced ids (`eh:12`, `c:34`); ours are plain numbers. */
const isExternalId = id => /^(eh|c):/.test(String(id || ''))

const HISTORY_PAGE = 200
const RECENT_PAGE = 100
const RECENT_REFRESH_MS = 60000

const KPI_TAB = {
  needs_driver: 'ACTIVE',
  late: 'ACTIVE',
  in_transit: 'IN_TRANSIT',
  avg_delivery: 'DELIVERED',
  delivered_today: 'DELIVERED',
}

/** Per-viewer view preference; storage can be blocked, so every access is guarded. */
function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw != null ? JSON.parse(raw) : initial
    } catch (e) {
      return initial
    }
  })
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch (e) {
      // Private mode or blocked storage: the preference just won't stick.
    }
  }, [key, value])
  return [value, setValue]
}

const isTyping = el => !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** The whole platform's board, or with `restaurantId` one restaurant's. */
const Dispatch = ({ restaurantId = null }) => {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const client = useApolloClient()
  const src = useMemo(() => boardSource(restaurantId), [restaurantId])
  const tabs = BOARD_TABS
  const allColumns = restaurantId ? RESTAURANT_COLUMNS : COLUMNS
  const typeTabs = restaurantId ? RESTAURANT_TYPE_TABS : FULFILMENT_TABS
  const channels = restaurantId ? RESTAURANT_CHANNELS : CHANNELS

  // ── View state ──────────────────────────────────────────────────────────
  const [tab, setTab] = useState('ACTIVE')
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [sort, setSort] = useState({ key: 'urgency', dir: 'asc' })
  const [density, setDensity] = usePersistentState('dispatch.density', 'comfortable')
  const [hiddenColumns, setHiddenColumns] = usePersistentState('dispatch.hiddenColumns', [])
  const [splitView, setSplitView] = usePersistentState('dispatch.splitView', false)
  const [pageSize, setPageSize] = usePersistentState('dispatch.pageSize', 50)
  // Not remembered: a board someone paused once shouldn't open frozen next time.
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState(() => new Set())
  const [openId, setOpenId] = useState(null)
  const [hoveredId, setHoveredId] = useState(null)
  const [cancelTargets, setCancelTargets] = useState(null)
  const [fdTargets, setFdTargets] = useState(null)
  const [rmfTargets, setRmfTargets] = useState(null)
  const [newDeliveryOpen, setNewDeliveryOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const [lastUpdated, setLastUpdated] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const searchRef = useRef(null)
  const lastToggled = useRef(null)

  const [mutateUpdate] = useMutation(UPDATE_STATUS)
  const [mutateAssign] = useMutation(ASSIGN_RIDER)
  const [mutateDispatchFD] = useMutation(DISPATCH_TO_FD)
  const [mutateSendRmf] = useMutation(SEND_TO_RMF)

  // ── Data ────────────────────────────────────────────────────────────────
  // Active orders — every live row in one request. page is an index and the
  // resolver offsets by page * rowsPerPage, so this must stay page 0.
  const {
    data: dataActive, loading, error, networkStatus, refetch: refetchActive, startPolling, stopPolling,
  } = useQuery(GET_ACTIVE_ORDERS, {
    variables: src.activeVars,
    pollInterval: REFRESH_MS,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  // Tab totals, from one grouped count on the server.
  const { data: countsData, refetch: refetchCounts, startPolling: startCounts, stopPolling: stopCounts } = useQuery(src.counts, {
    variables: src.countsVars,
    pollInterval: 30000,
    fetchPolicy: 'cache-and-network',
  })

  // A restaurant's board shows a new order the moment it's placed, not at the next poll.
  useSubscription(SUB_NEW_ORDER, {
    variables: { restaurant: restaurantId },
    skip: !restaurantId,
    onData: () => {
      refetchActive()
      refetchCounts()
    },
  })

  // Old-app and email orders, shown among the board's own.
  const {
    data: ehungryData, refetch: refetchEhungry, startPolling: startEhungry, stopPolling: stopEhungry,
  } = useQuery(src.ehungry, {
    variables: src.externalVars,
    pollInterval: EXTERNAL_REFRESH_MS,
    fetchPolicy: 'cache-and-network',
  })
  const {
    data: emailData, refetch: refetchEmail, startPolling: startEmail, stopPolling: stopEmail,
  } = useQuery(src.email, {
    variables: src.externalVars,
    pollInterval: EXTERNAL_REFRESH_MS,
    fetchPolicy: 'cache-and-network',
  })
  const { data: merchantsData } = useQuery(COURIER_MERCHANT_RESTAURANTS, { skip: !!restaurantId, fetchPolicy: 'cache-first' })
  const [mutateEhungryStatus] = useMutation(EHUNGRY_UPDATE_STATUS)
  const { data: homeData } = useQuery(RESTAURANT_HOME, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-first',
  })
  const home = useMemo(() => {
    if (!restaurantId) return null
    const r = homeData?.restaurant
    const c = r?.location?.coordinates
    return {
      id: restaurantId,
      name: r?.name || null,
      image: r?.image || null,
      address: r?.address || null,
      phone: r?.phone || null,
      location: Array.isArray(c) && c.length >= 2 && c[0] != null && c[1] != null ? { lat: parseFloat(c[1]), lng: parseFloat(c[0]) } : null,
      zoneId: r?.zone?._id || null,
      timezone: r?.timezone || null,
      selfDelivery: !!r?.selfDelivery,
    }
  }, [restaurantId, homeData])

  useEffect(() => {
    if (autoRefresh) {
      startPolling(REFRESH_MS)
      startCounts(30000)
      startEhungry(EXTERNAL_REFRESH_MS)
      startEmail(EXTERNAL_REFRESH_MS)
    } else {
      stopPolling()
      stopCounts()
      stopEhungry()
      stopEmail()
    }
  }, [autoRefresh, startPolling, stopPolling, startCounts, stopCounts, startEhungry, stopEhungry, startEmail, stopEmail])

  // Each completed poll moves "Updated …" and the clock the ages are measured against.
  useEffect(() => {
    if (networkStatus === 7 && dataActive) {
      const t = Date.now()
      setLastUpdated(t)
      setNow(t)
    }
  }, [networkStatus, dataActive])

  // Ages keep counting between polls (and while auto-refresh is off).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  // Recent slice — orders placed since yesterday morning, newest first, one
  // small page (rarely two). Feeds "Avg. delivery" and "Delivered today"
  // without downloading the whole history.
  const [recent, setRecent] = useState([])
  const [recentLoading, setRecentLoading] = useState(true)
  const loadRecent = useCallback(async () => {
    const since = startOfDay(Date.now()) - DAY
    const acc = []
    try {
      for (let p = 0; p < 10; p++) {
        const res = await client.query({ query: src.pages, variables: src.pageVars(p, RECENT_PAGE), fetchPolicy: 'network-only' })
        const batch = src.pageOf(res?.data)
        acc.push(...batch)
        const last = batch[batch.length - 1]
        if (batch.length < RECENT_PAGE || !last || (ms(last.createdAt) ?? 0) < since) break
      }
      setRecent(acc)
    } catch (e) {
      // The two "today" tiles show "—" until the next try.
    } finally {
      setRecentLoading(false)
    }
  }, [client, src])
  useEffect(() => { loadRecent() }, [loadRecent])
  useEffect(() => {
    if (!autoRefresh) return undefined
    const id = setInterval(loadRecent, RECENT_REFRESH_MS)
    return () => clearInterval(id)
  }, [autoRefresh, loadRecent])

  // History — every delivered/cancelled order, paged 200 at a time. It waits
  // until something asks: a history tab the recent slice can't answer, or a
  // search (which looks across everything).
  const [allHistory, setAllHistory] = useState([])
  const [historyWanted, setHistoryWanted] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const { fetchMore: fetchHistoryPage, refetch: refetchHistoryPage } = useQuery(src.pages, {
    variables: src.pageVars(0, HISTORY_PAGE),
    fetchPolicy: 'network-only',
    skip: !historyWanted,
    onCompleted: async firstData => {
      const acc = [...src.pageOf(firstData)]
      let p = 1
      setHistoryLoading(true)
      try {
        // Loop until a page returns fewer rows than asked (end of data).
        while (p <= 200) {
          const res = await fetchHistoryPage({ variables: src.pageVars(p, HISTORY_PAGE) })
          const batch = src.pageOf(res?.data)
          if (batch.length === 0) break
          acc.push(...batch)
          if (batch.length < HISTORY_PAGE) break
          p += 1
        }
        setAllHistory(acc)
        setHistoryLoaded(true)
      } finally {
        setHistoryLoading(false)
      }
    },
  })

  // A "today" KPI or a today/yesterday date filter is fully answered by the recent slice.
  const recentCovers =
    filters.kpi === 'delivered_today' || filters.kpi === 'avg_delivery' || ['today', 'yesterday'].includes(filters.date.preset)
  const tabMeta = tabs.find(t => t.key === tab) || tabs.find(t => t.key === 'ACTIVE')
  const needsHistory = filters.search.trim().length > 0 || (tabMeta.history && !recentCovers)
  useEffect(() => {
    if (needsHistory) setHistoryWanted(true)
  }, [needsHistory])

  const { data: dataConfig } = useQuery(GET_CONFIG)
  const mapboxToken = dataConfig?.configuration?.mapboxToken || MAPBOX_TOKEN || ''

  // The open order's lines — heavy, so fetched only for the order on screen.
  // An old-app or email order carries its own.
  const drawerId = openId && !isExternalId(openId) ? openId : null
  const { data: itemsData, loading: itemsLoading } = useQuery(GET_ORDER_ITEMS, {
    variables: { id: drawerId },
    skip: !drawerId,
    fetchPolicy: 'cache-first',
  })
  const openItems = itemsData?.order?._id === drawerId ? itemsData?.order?.items ?? [] : []

  const refetchAll = useCallback(() => {
    refetchActive()
    refetchCounts()
    loadRecent()
    if (historyWanted) refetchHistoryPage()
  }, [refetchActive, refetchCounts, loadRecent, historyWanted, refetchHistoryPage])

  const refresh = () => {
    setRefreshing(true)
    Promise.allSettled([
      refetchActive(), refetchCounts(), loadRecent(), historyWanted ? refetchHistoryPage() : null,
      refetchEhungry(), refetchEmail(),
    ]).finally(() => setRefreshing(false))
  }

  // ── Derived ─────────────────────────────────────────────────────────────
  // Rebuilt as the clock moves: a pre-order joins the work in hand when its ticket prints.
  const views = useMemo(() => {
    const byId = new Map()
    // Active last: it polls every 15 s, so its copy of a live order wins.
    ;[...allHistory, ...recent, ...(dataActive?.getActiveOrdersWithPagination?.orders ?? [])].forEach(o => {
      if (o?._id) byId.set(o._id, o)
    })
    const own = Array.from(byId.values()).map(toView)

    // Each old-app or email order under its own restaurant (logo, map pin,
    // Restaurant filter): the board's restaurant, or on the super admin board
    // whichever one it's linked to, as our own orders describe it.
    const restaurantsById = new Map(own.map(v => [String(v.restaurant.id), v.restaurant]))
    const merchantRestaurant = new Map((merchantsData?.courierMerchants ?? []).map(m => [String(m._id), m.rushmyfoodRestaurantId]))
    const homeOf = id => (restaurantId ? home : id != null ? restaurantsById.get(String(id)) || { id: String(id) } : null)
    const external = [
      ...src.ehungryOf(ehungryData).map(o => ehungryToView(o, homeOf(o.restaurantId), now)),
      // A null status is one with no place on the board yet (NEEDS_REVIEW and the like).
      ...src.emailOf(emailData).map(o => courierToView(o, homeOf(merchantRestaurant.get(String(o.merchantId))), now)).filter(v => v.status),
    ]

    const all = [...own, ...external].map(v => (restaurantId ? seenByRestaurant(v) : v))
    // Deliveries only: a pickup, dine-in or counter ticket has nothing to dispatch.
    return all.filter(v => v.type === 'DELIVERY')
  }, [allHistory, recent, dataActive, restaurantId, home, src, ehungryData, emailData, merchantsData, now])

  const filtered = useMemo(() => applyFilters(views, filters, now), [views, filters, now])
  // "All" reads as a history — newest first, live and finished mixed together.
  // Urgency order is for the live tabs; on All it would put every open order
  // above every finished one.
  const effectiveSort = useMemo(
    () => ((tab === 'ALL' || tabMeta.source) && sort.key === 'urgency' ? { key: 'age', dir: 'asc' } : sort),
    [tab, tabMeta.source, sort],
  )
  const rows = useMemo(
    () => sortViews(filtered.filter(v => matchesTab(v, tab)), effectiveSort, now),
    [filtered, tab, effectiveSort, now],
  )
  const kpis = useMemo(() => computeKpis(views, now), [views, now])
  // Type tab counts: every filter except the type itself, within the current
  // status tab. Blank on a history tab until history is in.
  const typeCounts = useMemo(() => {
    if (tabMeta.history && !historyLoaded && !recentCovers) return null
    const base = applyFilters(views, { ...filters, fulfilment: 'ALL' }, now).filter(v => matchesTab(v, tab))
    return fulfilmentCounts(base)
  }, [views, filters, now, tab, tabMeta.history, historyLoaded, recentCovers])
  const busyDriverIds = useMemo(
    () => new Set(views.filter(v => isInTransit(v) && v.raw.rider?._id).map(v => String(v.raw.rider._id))),
    [views],
  )
  const restaurants = useMemo(() => {
    const byId = new Map()
    views.forEach(v => { if (v.restaurant.id && !byId.has(v.restaurant.id)) byId.set(v.restaurant.id, v.restaurant) })
    return [...byId.values()]
  }, [views])

  // Tab counts. Live tabs are counted from the active list, which holds every
  // live order. Finished tabs use the server's totals until history is loaded;
  // under a filter they're left blank rather than guessed.
  const counts = useMemo(() => {
    const server = countsData?.dispatchOrderCounts
    // An external tab's rows are not in `filtered` or in the server's grouped
    // counts, so null (no badge) rather than a confident, wrong 0 — its own
    // panel shows its totals.
    if (!dataActive && server) {
      return Object.fromEntries(tabs.map(t => [t.key, t.source ? null : server[t.server] ?? 0]))
    }
    const local = localCounts(filtered)
    if (historyLoaded || (filtersOn(filters) && recentCovers)) return local
    if (filtersOn(filters) || !server) return { ...local, DELIVERED: null, CANCELLED: null, ALL: null }
    // The server counts marketplace orders only; a restaurant's old-app and email ones are all on hand.
    const ext = localCounts(views.filter(v => v.external))
    return {
      ...local,
      DELIVERED: server.delivered + ext.DELIVERED,
      CANCELLED: server.cancelled + ext.CANCELLED,
      ALL: server.all + ext.ALL,
    }
  }, [countsData, dataActive, filters, filtered, views, historyLoaded, recentCovers, tabs])

  const staleCount = useMemo(() => rows.filter(v => isStale(v, now)).length, [rows, now])
  const staleStart =
    effectiveSort.key === 'urgency' && !filters.hideStale && staleCount > 0 ? rows.findIndex(v => urgencyRank(v, now) === 5) : -1
  const attention = useMemo(() => views.filter(v => isActive(v) && (isLate(v, now) || needsDriver(v, now))).length, [views, now])
  const columns = useMemo(
    () => allColumns.map(c => c.id).filter(id => !hiddenColumns.includes(id) && !(splitView && SPLIT_HIDDEN.includes(id))),
    [allColumns, hiddenColumns, splitView],
  )
  const selectedViews = useMemo(() => rows.filter(v => selected.has(v.id)), [rows, selected])
  const openView = openId ? views.find(v => v.id === openId) || null : null
  const openIndex = rows.findIndex(v => v.id === openId)
  const mapViews = useMemo(() => rows.filter(v => isActive(v) && !isStale(v, now) && v.status !== 'SCHEDULED'), [rows, now])
  const staleOffMap = rows.filter(v => isActive(v) && isStale(v, now)).length

  // Back to page one whenever the list itself changes shape.
  useEffect(() => setPage(0), [tab, filters, sort, pageSize])

  // ── Actions ─────────────────────────────────────────────────────────────
  const settle = async (targets, fn) => {
    const results = await Promise.allSettled(targets.map(fn))
    const failed = results.map((r, i) => (r.status === 'rejected' ? { view: targets[i], error: r.reason } : null)).filter(Boolean)
    return { ok: targets.length - failed.length, failed }
  }
  const reportFailures = (failed, what) => {
    if (!failed.length) return
    const first = failed[0]
    NotificationManager.error(
      `${failed.length === 1 ? `#${first.view.orderNo}` : `${failed.length} orders`}: ${first.error?.message || 'request failed'}`,
      `Couldn't ${what}`,
      5000,
    )
  }
  const name = targets => (targets.length === 1 ? `#${targets[0].orderNo}` : plural(targets.length, 'order'))

  const setStatus = useCallback(async (targets, status) => {
    const eligible = targets.filter(v => canMoveTo(v, status))
    if (!eligible.length) {
      NotificationManager.warning(`None of these can be marked ${statusMeta(status).label.toLowerCase()} from their current status.`, 'Nothing changed', 3000)
      return
    }
    const { ok, failed } = await settle(eligible, v => mutateUpdate({ variables: { id: v.id, orderStatus: status } }))
    if (ok) {
      const verb = status === 'CANCELLED' ? 'cancelled' : `marked ${statusMeta(status).label.toLowerCase()}`
      const skipped = targets.length - eligible.length
      const who = ok === eligible.length ? name(eligible) : `${ok} of ${eligible.length} orders`
      NotificationManager.success(`${who} ${verb}${skipped ? ` · ${skipped} skipped` : ''}`, 'Updated', 2500)
    }
    reportFailures(failed, 'update')
    setSelected(new Set())
    if (['DELIVERED', 'CANCELLED'].includes(status) && eligible.some(v => v.id === openId)) setOpenId(null)
    refetchAll()
  }, [mutateUpdate, openId, refetchAll])

  const assign = useCallback(async (targets, rider) => {
    const eligible = targets.filter(canAssign)
    if (!eligible.length) return
    const { ok, failed } = await settle(eligible, v => mutateAssign({ variables: { id: v.id, riderId: rider._id } }))
    if (ok) NotificationManager.success(`${rider.name} assigned to ${ok === eligible.length ? name(eligible) : `${ok} of ${eligible.length} orders`}`, 'Rider', 2500)
    reportFailures(failed, 'assign')
    setSelected(new Set())
    refetchAll()
  }, [mutateAssign, refetchAll])

  const confirmSendToRmf = async () => {
    const targets = (rmfTargets || []).filter(canSendToRushmyfood)
    if (!targets.length) {
      setRmfTargets(null)
      return
    }
    setBusy(true)
    const { ok, failed } = await settle(targets, v => mutateSendRmf({ variables: { id: v.id } }))
    setBusy(false)
    setRmfTargets(null)
    if (ok) NotificationManager.success(`${ok === targets.length ? name(targets) : `${ok} of ${targets.length} orders`} sent to RushMyFood Delivery`, 'Finding a driver', 3000)
    reportFailures(failed, 'send to RushMyFood Delivery')
    setSelected(new Set())
    refetchAll()
  }

  const confirmSendToFD = async () => {
    const targets = (fdTargets || []).filter(canSendToFirstDelivery)
    if (!targets.length) {
      setFdTargets(null)
      return
    }
    setBusy(true)
    const { ok, failed } = await settle(targets, v => mutateDispatchFD({ variables: { id: v.id } }))
    setBusy(false)
    setFdTargets(null)
    if (ok) NotificationManager.success(`${targets.length === 1 ? `Order #${targets[0].orderNo}` : plural(ok, 'order')} sent to First Delivery`, 'Dispatched', 3000)
    if (failed.length) NotificationManager.error(failed[0].error?.message || 'Request failed', 'First Delivery', 5000)
    setSelected(new Set())
    refetchAll()
  }

  // An old-app order's status writes through to the legacy app, as on the Orders page.
  const setOldAppStatus = useCallback(async (view, status) => {
    try {
      await mutateEhungryStatus({ variables: { id: view.raw._id, status } })
      NotificationManager.success(`#${view.orderNo} marked ${status}`, 'Updated', 2500)
      refetchEhungry()
    } catch (e) {
      NotificationManager.error(e.message, "Couldn't update", 5000)
    }
  }, [mutateEhungryStatus, refetchEhungry])

  const confirmCancel = async () => {
    const targets = cancelTargets || []
    setBusy(true)
    // Old-app orders cancel in the old app; ours through our own status change.
    const own = targets.filter(v => !v.external)
    if (own.length) await setStatus(own, 'CANCELLED')
    for (const v of targets.filter(t => t.external === 'EHUNGRY')) await setOldAppStatus(v, 'cancelled')
    setBusy(false)
    setCancelTargets(null)
  }

  const copy = useCallback(async (text, what) => {
    try {
      await navigator.clipboard.writeText(text)
      NotificationManager.success(text, `${what} copied`, 1500)
    } catch (e) {
      NotificationManager.error('Select it and press ⌘C instead.', "Couldn't copy", 3000)
    }
  }, [])

  const exportCsv = () => {
    const list = selectedViews.length ? selectedViews : rows
    downloadCsv(toCsv(list), `dispatch-${new Date().toISOString().slice(0, 10)}.csv`)
    NotificationManager.success(`${plural(list.length, 'order')} exported`, 'CSV', 2000)
  }

  const ui = useMemo(
    () => ({
      now,
      openId,
      busyDriverIds,
      openOrder: setOpenId,
      assign,
      requestFirstDelivery: setFdTargets,
      requestRushmyfood: setRmfTargets,
      setStatus,
      requestCancel: setCancelTargets,
      setOldAppStatus,
      copy,
      // Set on a restaurant's board: its own drivers only, and no First
      // Delivery hand-off (the server allows that to admins alone).
      restaurantId,
      firstDelivery: !restaurantId,
      // …and in its place, handing a self-delivery order to RushMyFood.
      rushmyfood: !!restaurantId,
    }),
    [now, openId, busyDriverIds, assign, setStatus, setOldAppStatus, copy, restaurantId],
  )

  // ── View handlers ───────────────────────────────────────────────────────
  const patchFilters = useCallback(patch => setFilters(f => ({ ...f, ...patch })), [])

  const selectKpi = k => {
    setFilters(f => ({ ...f, kpi: k }))
    if (k) setTab(KPI_TAB[k])
    setSort({ key: 'urgency', dir: 'asc' })
  }

  const changeTab = t => {
    setTab(t)
    setSelected(new Set())
    setFilters(f => (f.kpi && KPI_TAB[f.kpi] !== t ? { ...f, kpi: null } : f))
  }

  const onSort = key =>
    setSort(s => {
      if (s.key !== key) return { key, dir: FIRST_DIR[key] }
      if (s.dir === FIRST_DIR[key]) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      return { key: 'urgency', dir: 'asc' } // third click hands back to urgency
    })

  const onToggle = useCallback((id, shiftKey) => {
    setSelected(prev => {
      const next = new Set(prev)
      const on = !prev.has(id)
      if (shiftKey && lastToggled.current) {
        const a = rows.findIndex(v => v.id === lastToggled.current)
        const b = rows.findIndex(v => v.id === id)
        if (a >= 0 && b >= 0) {
          rows.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(v => (on ? next.add(v.id) : next.delete(v.id)))
          return next
        }
      }
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
    lastToggled.current = id
  }, [rows])

  const onToggleMany = (ids, on) =>
    setSelected(prev => {
      const next = new Set(prev)
      ids.forEach(id => (on ? next.add(id) : next.delete(id)))
      return next
    })

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setSort({ key: 'urgency', dir: 'asc' })
  }

  const stepDrawer = useCallback(dir => {
    const i = rows.findIndex(v => v.id === openId)
    const next = rows[i + dir]
    if (!next) return
    setOpenId(next.id)
    const pageOf = Math.floor((i + dir) / pageSize)
    if (pageOf !== page) setPage(pageOf)
  }, [rows, openId, page, pageSize])

  // Keyboard: "/" to search, Esc to close the panel, J/K or arrows to walk orders.
  useEffect(() => {
    const onKey = e => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      if (document.querySelector('.MuiPopover-root, .MuiDialog-root, .MuiMenu-root')) return
      if (e.key === '/') {
        e.preventDefault()
        if (searchRef.current) searchRef.current.focus()
      } else if (openId && e.key === 'Escape') {
        setOpenId(null)
      } else if (openId && (e.key === 'j' || e.key === 'ArrowDown')) {
        e.preventDefault()
        stepDrawer(1)
      } else if (openId && (e.key === 'k' || e.key === 'ArrowUp')) {
        e.preventDefault()
        stepDrawer(-1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openId, stepDrawer])

  // ── Empty state copy depends on why the list is empty ──────────────────
  const busyLoading = (!dataActive && loading) || (tabMeta.history && !recentCovers && !historyLoaded)
  const empty = filtersOn(filters) ? (
    <BlankState
      title={filters.search ? `Nothing matches “${filters.search}”` : 'No orders match these filters'}
      body={`Try a wider date range${restaurantId ? '' : ', another restaurant,'} or clear the filters to see every ${tabMeta.source ? tabMeta.label : tabMeta.label.toLowerCase()} order.`}
      action={<Box component="button" type="button" onClick={clearFilters} sx={{ border: `1px solid ${C.border}`, bgcolor: C.card, borderRadius: '8px', px: 1.5, height: 32, font: 'inherit', fontSize: 13, cursor: 'pointer', color: C.text }}>Clear filters</Box>}
    />
  ) : tab === 'PENDING' ? (
    <BlankState title="No new orders right now" body="New orders land here the moment they're placed. The board refreshes every 15 seconds." />
  ) : tab === 'ASSIGNED' ? (
    <BlankState title="Nobody is waiting on a pickup" body="Orders show here once a driver is assigned and before they reach the restaurant." />
  ) : tab === 'OLD_APP' ? (
    <BlankState title={`No ${OLD_APP_LABEL} deliveries`} body="Deliveries from the old RushMyFood app show here as they sync, about once a minute." />
  ) : tab === 'EMAIL' ? (
    <BlankState title="No email orders" body="Delivery orders from your partner's order emails show here once they're imported." />
  ) : (
    <BlankState title={`No ${tabMeta.label.toLowerCase()} orders`} body="When orders reach this status they'll show up here." />
  )

  const card = { bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: '12px' }

  return (
    <DispatchUiContext.Provider value={ui}>
      <NotificationContainer />
      <Box
        sx={{
          fontFamily: FONT, color: C.text, bgcolor: C.page, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', gap: 2, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, pb: { xs: 12, lg: 2.5 },
          height: { lg: '100vh' }, minHeight: { lg: 0 },
          '& button, & input': { fontFamily: FONT },
        }}
      >
        {/* Header */}
        <Box component="header" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box component="h1" sx={{ m: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', color: C.text }}>Dispatch</Box>
            <Box sx={{ fontSize: 13, color: C.muted, ...tnum }}>
              {!dataActive && loading ? 'Loading the board…' : (
                <>
                  {counts.ACTIVE ?? 0} active
                  {attention > 0 && <> · <Box component="span" sx={{ fontWeight: 500, color: AGE.late.ink }}>{attention} need attention now</Box></>}
                  {(historyLoading || (loading && dataActive)) && <CircularProgress size={11} sx={{ ml: 1, color: C.faint, verticalAlign: '-1px' }} />}
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%' }}>
            <LiveIndicator
              lastUpdated={lastUpdated}
              auto={autoRefresh}
              onAuto={setAutoRefresh}
              refreshing={refreshing}
              onRefresh={refresh}
              offline={!!error}
            />
            <ToggleButton
              value="map"
              selected={splitView}
              onChange={() => setSplitView(s => !s)}
              aria-label="Show map beside the list"
              sx={{
                display: { xs: 'none', lg: 'inline-flex' }, height: 36, px: 1.5, gap: 0.75, borderRadius: '10px', textTransform: 'none',
                fontSize: 13, fontWeight: 500, color: C.text, bgcolor: C.card, borderColor: C.border,
                '&.Mui-selected, &.Mui-selected:hover': { bgcolor: C.ink, color: '#fff', borderColor: C.ink },
              }}
            >
              <MapOutlinedIcon sx={{ fontSize: 18 }} />
              Map
            </ToggleButton>
            {/* A restaurant types in a delivery it was already paid for. */}
            {restaurantId && (
              <Button
                onClick={() => setNewDeliveryOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{ ...primaryButtonSx, height: 36, px: 1.75, fontSize: 13, borderRadius: '10px', whiteSpace: 'nowrap' }}
              >
                New delivery
              </Button>
            )}
          </Box>
        </Box>

        <KpiStrip kpis={kpis} active={filters.kpi} onSelect={selectKpi} loading={!dataActive && loading} recentLoading={recentLoading} />

        <Box sx={{ display: 'flex', gap: 2, flex: { lg: 1 }, minHeight: { lg: 0 } }}>
          <Box
            component="section"
            aria-label="Orders"
            sx={{ ...card, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: { lg: 0 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, borderBottom: `1px solid ${C.border}` }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, alignItems: { xl: 'center' }, gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <StatusTabs tabs={tabs} value={tab} counts={counts} onChange={changeTab} loading={!dataActive && loading} />
                </Box>
                <TextField
                  inputRef={searchRef}
                  size="small"
                  value={filters.search}
                  onChange={e => patchFilters({ search: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      patchFilters({ search: '' })
                      e.currentTarget.querySelector('input')?.blur()
                    }
                  }}
                  placeholder="Search order #, customer, address, driver…"
                  inputProps={{ 'aria-label': 'Search orders' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box component="kbd" sx={{ display: { xs: 'none', sm: 'inline-block' }, px: 0.75, border: `1px solid ${C.border}`, borderRadius: '4px', bgcolor: C.mutedBg, fontFamily: MONO, fontSize: 10, color: C.muted, lineHeight: '16px' }}>/</Box>
                      </InputAdornment>
                    ),
                    sx: { height: 36, fontSize: 13, borderRadius: '8px', bgcolor: C.card, '& fieldset': { borderColor: C.border } },
                  }}
                  sx={{ width: { xs: '100%', xl: 320 }, flexShrink: 0 }}
                />
              </Box>
              <TypeTabs tabs={typeTabs} value={filters.fulfilment} counts={typeCounts} onChange={fulfilment => patchFilters({ fulfilment })} />
              <FilterBar
                filters={filters}
                onChange={patchFilters}
                onClear={clearFilters}
                restaurants={restaurantId ? null : restaurants}
                columns={allColumns}
                channels={channels}
                staleCount={views.filter(v => isStale(v, now)).length}
                density={density}
                onDensity={setDensity}
                hiddenColumns={hiddenColumns}
                onToggleColumn={c => setHiddenColumns(h => (h.includes(c) ? h.filter(x => x !== c) : [...h, c]))}
                splitView={splitView}
              />
            </Box>

            <DispatchTable
              rows={rows}
              loading={busyLoading && rows.length === 0}
              error={error && !dataActive ? error.message : null}
              onRetry={refetchAll}
              density={density}
              columns={columns}
              sort={effectiveSort}
              onSort={onSort}
              selectedIds={selected}
              onToggle={onToggle}
              onToggleMany={onToggleMany}
              hoveredId={hoveredId}
              onHover={setHoveredId}
              page={page}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={setPageSize}
              staleStart={staleStart >= 0 ? staleStart : null}
              staleCount={staleCount}
              onHideStale={() => patchFilters({ hideStale: true })}
              empty={empty}
              splitView={splitView}
            />
          </Box>

          {splitView && (
            <Box component="aside" aria-label="Live map" sx={{ ...card, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', overflow: 'hidden', width: '42%', minWidth: 380, maxWidth: 760 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
                <Box>
                  <Box sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Live map</Box>
                  <Box sx={{ fontSize: 12, color: C.muted, ...tnum }}>
                    {plural(mapViews.length, 'order')} · {plural(mapViews.filter(isInTransit).length, 'driver')} on the road
                  </Box>
                </Box>
                {staleOffMap > 0 && <Box sx={{ fontSize: 12, color: C.muted }}>{staleOffMap} stale not shown</Box>}
              </Box>
              <DispatchMap views={mapViews} now={now} mapboxToken={mapboxToken} hoveredId={hoveredId} onHover={setHoveredId} onSelect={setOpenId} />
              <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${C.border}` }}><MapLegend /></Box>
            </Box>
          )}
        </Box>
      </Box>

      <OrderDrawer
        view={openView}
        items={openView?.external ? openView.items : openItems}
        itemsLoading={itemsLoading && !openView?.external}
        mapboxToken={mapboxToken}
        onClose={() => setOpenId(null)}
        onStep={stepDrawer}
        canStep={{ prev: openIndex > 0, next: openIndex >= 0 && openIndex < rows.length - 1 }}
      />

      <BulkActionBar
        selected={selectedViews}
        visibleCount={rows.length}
        onSelectAllVisible={() => setSelected(new Set(rows.map(v => v.id)))}
        onClear={() => setSelected(new Set())}
        onExport={exportCsv}
      />

      <CancelDialog views={cancelTargets} busy={busy} onConfirm={confirmCancel} onClose={() => setCancelTargets(null)} />
      <SendToFdDialog views={fdTargets} busy={busy} onConfirm={confirmSendToFD} onClose={() => setFdTargets(null)} />
      <SendToRmfDialog views={rmfTargets} busy={busy} onConfirm={confirmSendToRmf} onClose={() => setRmfTargets(null)} />
      {restaurantId && (
        <NewDeliveryDialog
          open={newDeliveryOpen}
          restaurantId={restaurantId}
          home={home}
          mapboxToken={mapboxToken}
          onClose={() => setNewDeliveryOpen(false)}
          onCreated={order => {
            setNewDeliveryOpen(false)
            NotificationManager.success(
              order.orderStatus === 'SCHEDULED' ? 'It waits as Scheduled until it is due.' : 'Finding a driver now.',
              `Delivery #${order.orderId} created`,
              3500,
            )
            refetchAll()
          }}
        />
      )}
    </DispatchUiContext.Provider>
  )
}

export default withTranslation()(Dispatch)
